import moment from 'moment';

export const convertDateToDDMMYYYY = (date: string | Date) => {
  if (typeof date !== 'string') {
    return moment(date).toDate().toISOString();
  } else {
    if (isIsoDate(date)) {
      return date;
    }

    return moment(new Date(moment(date).format('DD-MM-YYYY')))
      .toDate()
      .toISOString();
  }
};

const isIsoDate = (str: string) => {
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
  const d = new Date(str);
  return d instanceof Date && !isNaN(d.getTime()) && d.toISOString() === str; // valid date
};

export const dateWithoutTime = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

// The bank only accepts a payment/transfer scheduled up to 90 days out - shared by every
// scheduling date picker (Scheduled Payment, Schedule Transfer, Batch Payment) so the cap can't
// drift out of sync between them.
export const MAX_SCHEDULE_DAYS_IN_ADVANCE = 90;

export const getMaxScheduleDate = (from: Date = new Date()) =>
  moment(from).add(MAX_SCHEDULE_DAYS_IN_ADVANCE, 'days').toDate();

export const evaluateIfSameDay = (firstDate: Date, secondDate: Date) => {
  const cleanedUpFirstDate = dateWithoutTime(firstDate);
  const cleanedUpSecondDate = dateWithoutTime(secondDate);

  return cleanedUpFirstDate.getTime() === cleanedUpSecondDate.getTime();
};

export const convertToZonedDateTimeFormat = (date: Date) =>
  moment(date).format('YYYY-MM-DD[T]HH:mm:ss.SSSZZ');

export const NZ_TIMEZONE = 'Pacific/Auckland';

export type TNzDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

/**
 * Projects an instant onto the New Zealand wall clock.
 *
 * A `Date` is an absolute instant, so `getHours()` etc. resolve against whatever timezone the
 * user's machine is set to - useless for rules defined in NZ time. Using an IANA zone (rather
 * than a fixed offset) also keeps NZST/NZDT correct year round. `h23` forces a 24-hour clock,
 * without which `en-NZ` formats 12-hour and hour `7` would be ambiguous.
 */
export const getNzDateTimeParts = (date: Date): TNzDateTimeParts => {
  const parts = new Intl.DateTimeFormat('en-NZ', {
    timeZone: NZ_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);

  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute')
  };
};
